import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, waLink } from "../lib/api";
import Seo from "../lib/Seo";
import { Button } from "../components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import {
  ArrowRight, MessageCircle, Clock, GraduationCap, Users, FileText, Video,
  Sparkles, CheckCircle2, ShieldCheck, BookOpen, Award, Headphones, Compass,
  Check, ChevronDown, Zap, HeartHandshake, Star, CalendarClock, QrCode,
} from "lucide-react";

/* --------------------------------------------------- constants & content */

const WHY_LEARN = [
  { icon: Award, title: "10+ Years of Practical Experience" },
  { icon: Video, title: "Live Interactive Online Classes" },
  { icon: Compass, title: "Practical Horoscope Analysis" },
  { icon: FileText, title: "Real Case Studies" },
  { icon: BookOpen, title: "PDF Study Notes" },
  { icon: Headphones, title: "Recorded Sessions (where applicable)" },
  { icon: MessageCircle, title: "WhatsApp Student Support" },
  { icon: ShieldCheck, title: "Course Completion Certificate" },
  { icon: Sparkles, title: "Lifetime Learning Approach" },
];

const STUDENT_BENEFITS = [
  { icon: Users, title: "Live Doubt Solving", desc: "Get every question answered in live Q&A sessions." },
  { icon: FileText, title: "Practical Assignments", desc: "Weekly assignments to reinforce real chart reading." },
  { icon: Compass, title: "Case Study Discussions", desc: "Learn from real horoscopes in group workshops." },
  { icon: BookOpen, title: "Downloadable Notes", desc: "PDF study notes for every module — yours forever." },
  { icon: Sparkles, title: "Lifetime Learning Support", desc: "Ongoing WhatsApp support & alumni community." },
  { icon: ShieldCheck, title: "Ethical Astrology Training", desc: "Learn responsibly, with dharma at the center." },
];

const FAQS = [
  { q: "Who can join these courses?", a: "Anyone interested in Vedic astrology — students, working professionals, homemakers, healers, coaches, and aspiring astrologers. There is no age limit or academic prerequisite." },
  { q: "Do I need prior astrology knowledge?", a: "For the Foundation, Recorded Foundation, and Panchang courses — no prior knowledge is required. The Basic to Advanced and Weekend courses assume the fundamentals or completion of the foundation." },
  { q: "Will recordings be provided?", a: "Yes. All live sessions are recorded and shared with enrolled students within 24 hours. Access is granted for the full duration of the course (and lifetime access for the recorded course)." },
  { q: "Will I receive study material?", a: "Yes — comprehensive PDF study notes are provided module by module. Additional handouts, chart worksheets and reference sheets are also shared." },
  { q: "Is a certificate provided?", a: "Yes, a Course Completion Certificate is issued for the flagship live cohorts and the Weekend course after successful completion of assignments and case work." },
  { q: "How are classes conducted?", a: "Classes are conducted online via Zoom / Google Meet with an interactive Q&A. All schedules are shared in advance and recordings are available." },
  { q: "What payment methods are accepted?", a: "UPI, Google Pay, PhonePe, bank transfer and international payments (on request). Reach out on WhatsApp for payment details." },
];

const COMPARE = [
  { name: "Vedic Jyotish Foundation", duration: "20 sessions", type: "Live", fit: "Beginners", fee: "₹3,000", cert: true, wa: true },
  { name: "Basic to Advanced", duration: "30 sessions", type: "Live", fit: "Aspiring pros", fee: "₹15,000", cert: true, wa: true, badge: "Most Popular" },
  { name: "Medical Astrology", duration: "10 sessions", type: "Live · Sundays", fit: "Health-focused", fee: "₹7,500", cert: true, wa: true },
  { name: "Panchang Master", duration: "5 sessions", type: "Live", fit: "Daily practice", fee: "₹1,500", cert: false, wa: true },
  { name: "Recorded Foundation", duration: "Self-paced", type: "Recorded", fit: "Learn anytime", fee: "₹2,100", cert: false, wa: true },
  { name: "Weekend Course", duration: "8 months", type: "Live · Sundays", fit: "Professionals", fee: "₹15,000", cert: true, wa: true },
];

/* ------------------------------------------------ Cosmic backdrop for hero */
function CosmicBackdrop() {
  return (
    <div aria-hidden="true" className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute inset-0 bg-gradient-to-br from-[#0B1F3A] via-[#0B1F3A] to-[#062036]" />
      <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#0E9F6E]/12 blur-[120px]" />
      <div className="absolute -bottom-32 right-0 w-[520px] h-[520px] rounded-full bg-[#D4AF37]/10 blur-[120px]" />
      {[...Array(38)].map((_, i) => (
        <span key={i} className="star" style={{
          width: `${Math.random() * 2 + 1}px`, height: `${Math.random() * 2 + 1}px`,
          left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`,
          animationDelay: `${Math.random() * 4}s`,
        }} />
      ))}
      <svg viewBox="0 0 500 500" className="absolute -right-40 top-10 w-[620px] opacity-15 wheel-spin">
        <circle cx="250" cy="250" r="220" fill="none" stroke="#D4AF37" strokeWidth="1.2" strokeDasharray="2 6" />
        <circle cx="250" cy="250" r="180" fill="none" stroke="#D4AF37" strokeOpacity="0.7" />
        <circle cx="250" cy="250" r="140" fill="none" stroke="#10B981" strokeOpacity="0.5" />
      </svg>
    </div>
  );
}

/* ---------------------------------------------------------- Course Card */
function CourseCard({ c, index }) {
  const enrollLabel = c.type === "recorded" ? "Buy Now" : "Enroll Now";
  const waMsg = `Hello, I would like to enroll in the ${c.title} (₹${c.fees.toLocaleString()}). Please share the next batch details.`;
  const popular = c.popular;
  return (
    <div data-testid={`course-card-${c.id}`} className={`group relative rounded-3xl overflow-hidden bg-background border transition-all hover:-translate-y-1 hover:shadow-2xl flex flex-col ${popular ? "border-[#D4AF37] shadow-xl md:scale-[1.02]" : "border-foreground/10 hover:border-[#D4AF37]/60"}`}>
      {popular && (
        <div className="absolute top-4 right-4 z-10 px-3 py-1 rounded-full bg-[#D4AF37] text-[#0B1F3A] text-[10px] uppercase tracking-[0.24em] font-bold shadow-lg">
          Most Popular
        </div>
      )}

      <div className="aspect-[16/10] overflow-hidden relative">
        <img src={c.cover_image} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F3A]/70 via-transparent to-transparent" />
        {c.badge && !popular && (
          <div className="absolute bottom-4 left-4 px-3 py-1 rounded-full bg-[#10B981]/95 text-white text-[10px] uppercase tracking-[0.22em] font-semibold">
            {c.badge}
          </div>
        )}
      </div>

      <div className="p-6 md:p-7 flex flex-col flex-1">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className={`text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 rounded-full ${c.type === "live" ? "bg-[#D4AF37] text-[#0B1F3A]" : "bg-[#0F284B] text-[#F5E8D8]"}`}>{c.type === "live" ? "Live" : "Recorded"}</span>
          <span className="text-[10px] uppercase tracking-[0.22em] text-foreground/60 flex items-center gap-1"><GraduationCap className="w-3 h-3" />{c.level}</span>
        </div>

        <h3 className="font-serif text-2xl md:text-[1.7rem] leading-tight mb-2">{c.title}</h3>
        <p className="text-sm text-foreground/70 mb-4 leading-relaxed">{c.description}</p>

        <div className="grid grid-cols-2 gap-2 mb-5 text-xs">
          <div className="p-2.5 rounded-xl bg-[#F4EFE6] dark:bg-[#0F284B]">
            <div className="text-[9px] uppercase tracking-[0.2em] text-foreground/60">Duration</div>
            <div className="mt-0.5 font-semibold">{c.duration}</div>
          </div>
          <div className="p-2.5 rounded-xl bg-[#F4EFE6] dark:bg-[#0F284B]">
            <div className="text-[9px] uppercase tracking-[0.2em] text-foreground/60">Class</div>
            <div className="mt-0.5 font-semibold">{c.class_duration || "—"}</div>
          </div>
        </div>

        <details className="mb-5 group/syl" data-testid={`syllabus-${c.id}`}>
          <summary className="cursor-pointer list-none flex items-center justify-between p-3 rounded-2xl border border-foreground/10 hover:border-[#D4AF37]/60 transition-colors">
            <span className="text-sm font-semibold">View Syllabus ({c.modules.length} topics)</span>
            <ChevronDown className="w-4 h-4 group-open/syl:rotate-180 transition-transform text-[#D4AF37]" />
          </summary>
          <ul className="mt-3 space-y-2 max-h-64 overflow-y-auto pr-1">
            {c.modules.map((m, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <Check className="w-4 h-4 mt-0.5 shrink-0 text-[#0E9F6E]" />
                <span className="text-foreground/80">{m}</span>
              </li>
            ))}
          </ul>
        </details>

        {c.best_for && (
          <div className="mb-5 text-xs text-foreground/70 italic">
            <span className="text-[#AD974F] font-semibold not-italic">Best for: </span>{c.best_for}
          </div>
        )}

        <div className="mt-auto flex items-end justify-between mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-foreground/60">Course Fee</div>
            <div className="font-serif text-4xl text-[#D4AF37] leading-none mt-1">₹{c.fees.toLocaleString()}</div>
          </div>
          <Link to={`/courses/${c.id}`} className="text-xs text-foreground/60 hover:text-[#D4AF37] underline underline-offset-4">Full details</Link>
        </div>

        <a href={waLink(waMsg)} target="_blank" rel="noreferrer" data-testid={`enroll-${c.id}`}>
          <Button size="lg" className={`w-full rounded-full font-semibold py-6 ${popular ? "bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A]" : "bg-[#0B1F3A] hover:bg-[#0F284B] text-[#F5E8D8] dark:bg-[#D4AF37] dark:hover:bg-[#AD974F] dark:text-[#0B1F3A]"}`}>
            {enrollLabel} <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </a>
      </div>
    </div>
  );
}

/* ================================================================ PAGE */
export default function Courses() {
  const [courses, setCourses] = useState([]);
  const [showStickyCta, setShowStickyCta] = useState(false);
  const coursesRef = useRef(null);

  // SEO
  useEffect(() => {
    document.title = "Learn Vedic Astrology Online — Courses by Bhupaal Bisht | YourAstroHub";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Learn authentic Vedic astrology online with Bhupaal Bisht — live Jyotish courses, medical astrology, Panchang mastery, recorded foundation & 8-month weekend program. Certificates, PDF notes, WhatsApp support.");
    const kw = document.querySelector('meta[name="keywords"]');
    if (kw) kw.setAttribute("content", "Vedic Astrology Course, Learn Jyotish Online, Medical Astrology Course, Panchang Course, Online Astrology Classes, Vedic Jyotish Certification, Bhupaal Bisht");
    return () => { document.title = "YourAstroHub — Decode Your Destiny with Vedic Wisdom"; };
  }, []);

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data));
  }, []);

  useEffect(() => {
    const onScroll = () => setShowStickyCta(window.scrollY > 700);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToCourses = () => coursesRef.current?.scrollIntoView({ behavior: "smooth" });

  const featuredCourse = courses.find((c) => c.popular) || courses[0];

  return (
    <div data-testid="courses-page" className="min-h-screen">
      <Seo
        title="Learn Vedic Astrology Online — Jyotish Courses by Bhupaal Bisht | YourAstroHub"
        description="Master authentic Vedic Astrology through live and recorded Jyotish courses — Foundation, Basic-to-Advanced, Medical Astrology, Panchang Master and 8-month Weekend cohort. Certificates, PDF notes, WhatsApp support."
        keywords="Vedic Astrology Course, Learn Vedic Astrology Online, Jyotish Course, Astrology Classes, Astrology Certification, Medical Astrology Course, Panchang Course, Astrology Foundation Course, Bhupaal Bisht"
        path="/courses"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Courses", path: "/courses" },
        ]}
        schemas={[{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }, ...courses.map((c) => ({
          "@context": "https://schema.org",
          "@type": "Course",
          name: c.title,
          description: c.description,
          provider: { "@type": "Organization", name: "YourAstroHub", sameAs: "https://vedic-destiny-hub-2.preview.emergentagent.com/" },
          offers: {
            "@type": "Offer",
            price: c.fees,
            priceCurrency: "INR",
            availability: "https://schema.org/InStock",
          },
          hasCourseInstance: {
            "@type": "CourseInstance",
            courseMode: c.type === "live" ? "Online (Live)" : "Online (Self-paced)",
            instructor: { "@type": "Person", name: "Bhupaal Bisht" },
          },
        }))]}
      />
      {/* HERO -------------------------------------------------------- */}
      <section className="relative overflow-hidden -mt-20 pt-32 md:pt-40 pb-24 md:pb-32 text-[#F5E8D8] bg-[#0B1F3A]">
        <CosmicBackdrop />
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-8 text-center">
          <div className="reveal eyebrow flex items-center justify-center gap-3 mb-6">
            <span className="w-8 gold-hairline" /> Learn Vedic Astrology <span className="w-8 gold-hairline" />
          </div>
          <h1 data-testid="courses-hero-title" className="reveal-2 font-serif text-4xl md:text-6xl lg:text-7xl font-light leading-[1.05]">
            Learn Authentic <span className="gold-text italic">Vedic Astrology</span>
          </h1>
          <p className="reveal-3 mt-6 text-base md:text-lg text-[#F5E8D8]/75 max-w-3xl mx-auto leading-relaxed">
            Master the timeless science of Vedic Astrology through structured, practical, and interactive
            courses taught by <b className="text-[#D4AF37]">Bhupaal Bisht</b>. Whether you are a beginner,
            an aspiring professional astrologer, or simply wish to understand your own horoscope, there is
            a course designed for you.
          </p>
          <div className="reveal-4 mt-9 flex flex-wrap gap-3 justify-center">
            <button onClick={scrollToCourses} data-testid="hero-enroll-cta">
              <Button size="lg" className="rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold px-8 py-6">
                Enroll Now <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </button>
            <a href={waLink("Hello, I would like to know more about the Vedic astrology courses at YourAstroHub.")} target="_blank" rel="noreferrer" data-testid="hero-wa-cta">
              <Button size="lg" className="rounded-full bg-[#25D366] hover:bg-[#20b957] text-white font-semibold px-8 py-6">
                <MessageCircle className="w-4 h-4 mr-2" />Contact on WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* WHY LEARN --------------------------------------------------- */}
      <section data-testid="why-learn" className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="eyebrow mb-3">Why Learn from YourAstroHub?</div>
            <h2 className="font-serif text-4xl md:text-5xl font-light">
              Nine reasons students <span className="gold-text italic">choose us.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {WHY_LEARN.map((w, i) => (
              <div key={w.title} data-testid={`whyl-${i}`} className="group flex items-center gap-4 p-5 rounded-2xl bg-background border border-foreground/10 hover:border-[#0E9F6E]/60 transition-all hover:-translate-y-1">
                <div className="shrink-0 w-11 h-11 rounded-full grid place-items-center bg-[#10B981]/10 border border-[#10B981]/30 group-hover:bg-[#D4AF37]/15 group-hover:border-[#D4AF37]/40 transition-colors">
                  <w.icon className="w-5 h-5 text-[#0E9F6E] group-hover:text-[#D4AF37] transition-colors" />
                </div>
                <div className="font-serif text-lg md:text-xl leading-tight">{w.title}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COURSES ----------------------------------------------------- */}
      <section id="all-courses" ref={coursesRef} data-testid="all-courses" className="py-24 bg-[#F4EFE6] dark:bg-[#0F284B]">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="eyebrow mb-3">Featured Courses</div>
            <h2 className="font-serif text-4xl md:text-5xl font-light">
              Six programs. <span className="emerald-text italic">One authentic path.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((c, i) => <CourseCard key={c.id} c={c} index={i} />)}
          </div>
        </div>
      </section>

      {/* PAYMENT CALLOUT ---------------------------------------- */}
      <section data-testid="payment-callout" className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-6 md:px-8">
          <div className="rounded-3xl border-2 border-[#D4AF37]/40 bg-[#0B1F3A] text-[#F5E8D8] p-8 md:p-10 grid md:grid-cols-[auto_1fr_auto] gap-6 items-center relative overflow-hidden grain">
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#10B981]/25 blur-3xl" />
            <div className="relative w-14 h-14 rounded-2xl grid place-items-center bg-[#D4AF37]/15 border border-[#D4AF37]/40">
              <QrCode className="w-6 h-6 text-[#D4AF37]" />
            </div>
            <div className="relative">
              <div className="eyebrow mb-2">Ready to Enroll?</div>
              <h3 className="font-serif text-2xl md:text-3xl leading-tight">Complete your course payment securely</h3>
              <p className="text-sm text-[#F5E8D8]/70 mt-2">Pay via UPI, Google Pay, Paytm, PhonePe or bank transfer. Upload proof to activate access.</p>
            </div>
            <Link to="/payment" data-testid="go-to-payment-courses" className="relative">
              <Button size="lg" className="rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold px-8 py-6">
                Go to Payment <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* COMPARISON TABLE ------------------------------------------- */}
      <section data-testid="compare-section" className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="eyebrow mb-3">Compare Courses</div>
            <h2 className="font-serif text-4xl md:text-5xl font-light">Find the right <span className="gold-text italic">fit for you.</span></h2>
          </div>

          <div className="overflow-x-auto rounded-3xl border border-foreground/10">
            <table className="w-full text-sm">
              <thead className="bg-[#0B1F3A] text-[#F5E8D8]">
                <tr>
                  {["Course", "Duration", "Live / Recorded", "Suitable For", "Fee", "Certificate", "WhatsApp"].map((h) => (
                    <th key={h} className="text-left px-5 py-4 font-semibold uppercase text-[10px] tracking-[0.22em]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARE.map((r, i) => (
                  <tr key={r.name} data-testid={`compare-row-${i}`} className={`border-t border-foreground/10 hover:bg-foreground/5 ${r.badge ? "bg-[#D4AF37]/5" : ""}`}>
                    <td className="px-5 py-4">
                      <div className="font-serif text-lg leading-tight">{r.name}</div>
                      {r.badge && <div className="mt-1 inline-block text-[9px] uppercase tracking-[0.24em] px-2 py-0.5 rounded-full bg-[#D4AF37] text-[#0B1F3A] font-bold">{r.badge}</div>}
                    </td>
                    <td className="px-5 py-4 text-foreground/80">{r.duration}</td>
                    <td className="px-5 py-4 text-foreground/80">{r.type}</td>
                    <td className="px-5 py-4 text-foreground/80">{r.fit}</td>
                    <td className="px-5 py-4 text-[#D4AF37] font-serif text-xl">{r.fee}</td>
                    <td className="px-5 py-4">{r.cert ? <CheckCircle2 className="w-5 h-5 text-[#0E9F6E]" /> : <span className="text-foreground/40 text-xs">—</span>}</td>
                    <td className="px-5 py-4">{r.wa ? <CheckCircle2 className="w-5 h-5 text-[#0E9F6E]" /> : <span className="text-foreground/40 text-xs">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* STUDENT BENEFITS ------------------------------------------- */}
      <section data-testid="student-benefits" className="py-24 bg-[#0B1F3A] text-[#F5E8D8] relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-30" />
        <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full bg-[#10B981]/15 blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-[#D4AF37]/15 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="eyebrow mb-3">Student Benefits</div>
            <h2 className="font-serif text-4xl md:text-5xl font-light">Beyond the syllabus. <span className="gold-text italic">A community.</span></h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {STUDENT_BENEFITS.map((b, i) => (
              <div key={b.title} data-testid={`benefit-${i}`} className="p-7 rounded-3xl bg-[#0F284B]/70 backdrop-blur border border-[#D4AF37]/25 hover:border-[#D4AF37]/60 transition-all hover:-translate-y-1">
                <div className="w-11 h-11 rounded-2xl grid place-items-center bg-[#D4AF37]/15 border border-[#D4AF37]/40 mb-5">
                  <b.icon className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <h3 className="font-serif text-xl leading-tight mb-2">{b.title}</h3>
                <p className="text-sm text-[#F5E8D8]/70 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ -------------------------------------------------------- */}
      <section data-testid="courses-faq" className="py-24 bg-background">
        <div className="max-w-4xl mx-auto px-6 md:px-8">
          <div className="text-center mb-12">
            <div className="eyebrow mb-3">Frequently Asked Questions</div>
            <h2 className="font-serif text-4xl md:text-5xl font-light">Everything you need to <span className="gold-text italic">know.</span></h2>
          </div>
          <Accordion type="single" collapsible className="w-full" data-testid="courses-faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-b border-foreground/10">
                <AccordionTrigger data-testid={`courses-faq-q-${i}`} className="text-left font-serif text-lg md:text-xl py-5 hover:text-[#D4AF37] hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-foreground/75 text-base leading-relaxed pb-6">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA -------------------------------------------------- */}
      <section data-testid="courses-final-cta" className="pb-24">
        <div className="max-w-6xl mx-auto px-6 md:px-8">
          <div className="relative rounded-3xl overflow-hidden p-10 md:p-16 bg-[#0B1F3A] text-[#F5E8D8] grain text-center">
            <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#10B981]/25 blur-3xl rounded-full" />
            <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-[#D4AF37]/25 blur-3xl rounded-full" />
            <div className="relative">
              <Sparkles className="w-8 h-8 text-[#D4AF37] mx-auto mb-4" />
              <h3 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-tight max-w-3xl mx-auto">
                Start Your Journey into the World of <span className="gold-text italic">Vedic Astrology</span> Today
              </h3>
              <p className="mt-5 text-[#F5E8D8]/75 max-w-2xl mx-auto text-lg leading-relaxed">
                Whether you wish to understand your own horoscope or become a professional astrologer, YourAstroHub
                provides structured, practical, and authentic Vedic astrology education.
              </p>
              <div className="mt-9 flex flex-wrap gap-3 justify-center">
                <button onClick={scrollToCourses} data-testid="final-enroll">
                  <Button size="lg" className="rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold px-8 py-6">
                    Enroll Now <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </button>
                <a href={waLink("Hello, I would like to enroll in a Vedic astrology course.")} target="_blank" rel="noreferrer" data-testid="final-wa">
                  <Button size="lg" className="rounded-full bg-[#25D366] hover:bg-[#20b957] text-white font-semibold px-8 py-6">
                    <MessageCircle className="w-4 h-4 mr-2" />Chat on WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STICKY ENROLL BUTTON */}
      {showStickyCta && (
        <button onClick={scrollToCourses} data-testid="sticky-enroll" aria-label="Enroll Now"
          className="fixed bottom-24 md:bottom-6 left-6 z-40">
          <div className="flex items-center gap-2 px-5 py-3 rounded-full bg-[#D4AF37] text-[#0B1F3A] font-semibold shadow-2xl border border-[#0B1F3A]/10 hover:bg-[#AD974F] transition-all">
            <GraduationCap className="w-4 h-4" />Enroll Now
          </div>
        </button>
      )}
    </div>
  );
}
